"use client";

import { useTranslations } from "next-intl";
import { Check, ChevronLeft } from "lucide-react";
import { getLocaleConfig, type Locale } from "@/config/locale";
import { cn } from "@/lib/utils";
import { FooterLocaleFlag } from "@/ui/components/footer-region-flags";

export interface LanguageDetailPanelProps {
	currentLocale: Locale;
	/** The gate's enabled set, passed down as data — never read from the registry here. */
	enabledLocales: readonly Locale[];
	onBack: () => void;
	onSelect: (locale: Locale) => void;
}

export function LanguageDetailPanel({
	currentLocale,
	enabledLocales,
	onBack,
	onSelect,
}: LanguageDetailPanelProps) {
	const t = useTranslations("localeChannel");

	return (
		<div className="flex h-full flex-col">
			<div className="bg-muted/30 flex items-center gap-2 border-b border-border px-5 py-4">
				<button
					type="button"
					onClick={onBack}
					aria-label={t("back")}
					className="hover:bg-accent/40 -ml-1 inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground"
				>
					<ChevronLeft className="h-4 w-4" />
				</button>
				<h3 className="text-sm font-semibold text-foreground">{t("languageLabel")}</h3>
			</div>

			<div className="flex-1 overflow-y-auto overscroll-contain px-3 pb-4 pt-4">
				{enabledLocales.map((locale) => {
					const config = getLocaleConfig(locale);
					const isActive = locale === currentLocale;
					return (
						<button
							key={locale}
							type="button"
							aria-current={isActive ? "true" : undefined}
							onClick={() => onSelect(locale)}
							className={cn(
								"flex min-h-[44px] w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors",
								isActive ? "bg-primary text-primary-foreground" : "hover:bg-muted/50 text-foreground",
							)}
						>
							<FooterLocaleFlag locale={locale} className="h-4 w-6 shrink-0 rounded-[2px]" />
							<span className="min-w-0 flex-1 truncate font-medium">{config.nativeName}</span>
							{isActive ? <Check className="h-4 w-4 shrink-0" aria-hidden /> : null}
						</button>
					);
				})}
			</div>
		</div>
	);
}
